import type { IChartingLibraryWidget } from "@public/charting_library";
import {
	type MutableRefObject,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	type chartThemeConfig,
	getChartOverrides,
} from "../constants/chart-theme.ts";
import { applyChartTheme, saveChartState } from "../lib/chart-widget.ts";

type UseChartSymbolSyncParams = {
	chartReadyRef: MutableRefObject<boolean>;
	currentSymbolRef: MutableRefObject<string>;
	isMobile: boolean;
	nextSymbol: string;
	onError?: (message: string) => void;
	onSuccess?: () => void;
	resolvedTheme: keyof typeof chartThemeConfig;
	widgetRef: RefObject<IChartingLibraryWidget | null>;
};

export function useChartSymbolSync({
	chartReadyRef,
	currentSymbolRef,
	isMobile,
	nextSymbol,
	onError,
	onSuccess,
	resolvedTheme,
	widgetRef,
}: UseChartSymbolSyncParams) {
	const [isSwitchingSymbol, setIsSwitchingSymbol] = useState(false);
	const symbolChangeRequestRef = useRef(0);
	const onErrorRef = useRef(onError);
	const onSuccessRef = useRef(onSuccess);
	onErrorRef.current = onError;
	onSuccessRef.current = onSuccess;

	useEffect(() => {
		const widget = widgetRef.current;
		const currentSymbol = currentSymbolRef.current;

		if (!widget || !chartReadyRef.current || currentSymbol === nextSymbol) {
			return;
		}

		let cancelled = false;
		const requestId = ++symbolChangeRequestRef.current;
		const shouldAbort = () =>
			cancelled ||
			requestId !== symbolChangeRequestRef.current ||
			widgetRef.current !== widget ||
			!chartReadyRef.current;

		setIsSwitchingSymbol(true);

		const switchSymbol = async () => {
			await saveChartState(widget);

			if (shouldAbort()) {
				return;
			}

			try {
				const activeChart = widget.activeChart();
				const didSetSymbol = await activeChart.setSymbol(nextSymbol);

				if (!didSetSymbol) {
					if (!shouldAbort()) {
						onErrorRef.current?.("Unable to load chart data");
					}
					return;
				}

				if (shouldAbort()) {
					return;
				}

				currentSymbolRef.current = nextSymbol;
				onSuccessRef.current?.();
				applyChartTheme(widget, getChartOverrides(resolvedTheme, isMobile));
			} catch (error) {
				console.warn(
					`[Chart] Failed to switch symbol to "${nextSymbol}".`,
					error,
				);
				if (!shouldAbort()) {
					onErrorRef.current?.(
						error instanceof Error
							? error.message
							: "Unable to load chart data",
					);
				}
			} finally {
				if (!cancelled && requestId === symbolChangeRequestRef.current) {
					setIsSwitchingSymbol(false);
				}
			}
		};

		void switchSymbol();

		return () => {
			cancelled = true;
		};
	}, [
		chartReadyRef,
		currentSymbolRef,
		isMobile,
		nextSymbol,
		resolvedTheme,
		widgetRef,
	]);

	return { isSwitchingSymbol };
}
